import { useCallback, useEffect, useState } from 'react';
import { Send, Bell, CalendarClock } from 'lucide-react';
import { FincurryLogo } from '@/components/brand/fincurry-logo';
import { ADMIN_TIME_ZONE } from '@/lib/format/admin-time';
import { istWallClockToIso } from '@/lib/notifications/schedule-request';
import { UserPicker, type RecipientUser } from '@/components/notifications/user-picker';
import { adminFetch } from '@/api';

interface BroadcastRow {
  title: string;
  body: string;
  sentAt: string;
  recipientCount: number;
}

interface SegmentRow {
  id: string;
  label: string;
  description: string;
  count: number;
}

interface ScheduledRow {
  id: string;
  title: string;
  body: string;
  segment: string;
  segmentParams: { belowVersion?: string } | null;
  sendAt: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
  recipientCount: number | null;
  withToken: number | null;
  error: string | null;
}

const DEFAULT_SEND_TIME = '14:00';
/** Client-only audience: hand-picked users instead of a segment. */
const SPECIFIC_USERS = 'specific_users';
const VERSION_RE = /^\d+\.\d+\.\d+$/;

/** Today in IST as YYYY-MM-DD — or tomorrow once [time] has passed today. */
function defaultSendDate(time: string): string {
  const now = new Date();
  const todayIst = new Intl.DateTimeFormat('en-CA', { timeZone: ADMIN_TIME_ZONE }).format(now);
  const iso = istWallClockToIso(todayIst, time);
  if (iso && new Date(iso) > now) return todayIst;
  return new Intl.DateTimeFormat('en-CA', { timeZone: ADMIN_TIME_ZONE }).format(
    new Date(now.getTime() + 86_400_000),
  );
}

const STATUS_STYLE: Record<ScheduledRow['status'], string> = {
  pending: 'text-amber-400',
  sending: 'text-sky-400',
  sent: 'text-emerald-400',
  failed: 'text-red-400',
  cancelled: 'text-zinc-500',
};

async function fetchScheduled(): Promise<ScheduledRow[] | null> {
  const res = await adminFetch('/api/notifications/scheduled');
  if (!res.ok) return null;
  const data = (await res.json()) as { scheduled: ScheduledRow[] };
  return data.scheduled;
}

function formatIndian(n: number): string {
  return n.toLocaleString('en-IN');
}

function formatSentAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: ADMIN_TIME_ZONE,
    timeZoneName: 'short',
  });
}

export default function NotificationsPage() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [history, setHistory] = useState<BroadcastRow[]>([]);
  const [segments, setSegments] = useState<SegmentRow[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<string>('all');
  const [belowVersion, setBelowVersion] = useState('');
  const [mode, setMode] = useState<'now' | 'schedule'>('now');
  const [sendTime, setSendTime] = useState(DEFAULT_SEND_TIME);
  const [sendDate, setSendDate] = useState(() => defaultSendDate(DEFAULT_SEND_TIME));
  const [scheduled, setScheduled] = useState<ScheduledRow[]>([]);
  const [recipients, setRecipients] = useState<RecipientUser[]>([]);

  const isSpecific = selectedSegment === SPECIFIC_USERS;
  // Hand-picked sends go out now; only segments can be scheduled.
  const effectiveMode = isSpecific ? 'now' : mode;

  const versionValid = VERSION_RE.test(belowVersion);

  const loadSegments = useCallback(async (version: string) => {
    const query = VERSION_RE.test(version) ? `?belowVersion=${version}` : '';
    const res = await adminFetch(`/api/notifications/segments${query}`);
    if (!res.ok) return;
    const data = (await res.json()) as {
      segments: SegmentRow[];
      belowVersion: string | null;
    };
    setSegments(data.segments);
    // First load: prefill with the app-update config's latest version.
    if (!version && data.belowVersion) setBelowVersion(data.belowVersion);
  }, []);

  // Recount only for a complete version — not on every keystroke.
  const countVersion = versionValid ? belowVersion : '';
  useEffect(() => {
    loadSegments(countVersion);
  }, [loadSegments, countVersion]);

  const loadScheduled = useCallback(async () => {
    const rows = await fetchScheduled();
    if (rows) setScheduled(rows);
  }, []);

  useEffect(() => {
    let alive = true;
    fetchScheduled().then(rows => {
      if (alive && rows) setScheduled(rows);
    });
    return () => {
      alive = false;
    };
  }, []);

  const cancelScheduled = async (id: string) => {
    if (!confirm('Cancel this scheduled notification?')) return;
    await adminFetch(`/api/notifications/scheduled/${id}`, { method: 'DELETE' });
    loadScheduled();
  };

  const loadHistory = useCallback(async () => {
    const res = await adminFetch('/api/notifications/history?limit=20');
    if (!res.ok) return;
    const data = (await res.json()) as { broadcasts: BroadcastRow[] };
    setHistory(data.broadcasts);
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const sendAtIso = effectiveMode === 'schedule' ? istWallClockToIso(sendDate, sendTime) : null;
  const canSend =
    !!title.trim() &&
    !!body.trim() &&
    (selectedSegment !== 'below_version' || versionValid) &&
    (!isSpecific || recipients.length > 0) &&
    (effectiveMode === 'now' || !!sendAtIso);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    try {
      const res = await adminFetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          isSpecific
            ? { title, body, targetUserIds: recipients.map(u => u.id) }
            : {
                title,
                body,
                segment: selectedSegment,
                ...(selectedSegment === 'below_version' ? { belowVersion } : {}),
                ...(sendAtIso ? { sendAt: sendAtIso } : {}),
              },
        ),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        setResult({ type: 'error', message: err.error ?? 'Failed to send notification' });
        return;
      }
      const data = (await res.json()) as {
        sent?: number;
        push?: { attempted: number; withToken: number } | null;
        scheduled?: { id: string; sendAt: string };
      };
      if (data.scheduled) {
        setResult({
          type: 'success',
          message: `Scheduled for ${formatSentAt(data.scheduled.sendAt)} — recipients are picked at send time`,
        });
        setTitle('');
        setBody('');
        loadScheduled();
        return;
      }
      const pushLine = data.push
        ? ` · pushed to ${data.push.withToken} of ${data.push.attempted} (rest have no FCM token)`
        : ' · push skipped (server not configured)';
      setResult({
        type: 'success',
        message: `Logged for ${data.sent ?? 0} users${pushLine}`,
      });
      setTitle('');
      setBody('');
      setRecipients([]);
      loadHistory();
    } catch {
      setResult({ type: 'error', message: 'Failed to send notification' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[22px] font-bold text-white tracking-tight">Push Notifications</h1>
        <p className="text-zinc-500 text-[13px] mt-0.5">Send to a segment, or pick specific users</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Compose */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-semibold text-white">Compose Message</span>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-2">Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. New Feature Available!"
              className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all"
            />
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-2">Body</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Write your notification message..."
              rows={4}
              className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-2">Segment</label>
            <div className="grid grid-cols-1 gap-1.5">
              {segments.map(s => (
                <label
                  key={s.id}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-all ${
                    selectedSegment === s.id
                      ? 'border-emerald-500/40 bg-emerald-500/5'
                      : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.14]'
                  }`}
                >
                  <input
                    type="radio"
                    name="segment"
                    value={s.id}
                    checked={selectedSegment === s.id}
                    onChange={() => setSelectedSegment(s.id)}
                    className="accent-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium text-white">
                      {s.label}
                    </div>
                    <div className="text-[10.5px] text-zinc-500 truncate">
                      {s.description}
                    </div>
                  </div>
                  <span className="text-[11.5px] font-mono text-emerald-400">
                    {formatIndian(s.count)}
                  </span>
                </label>
              ))}
              {segments.length === 0 ? (
                <div className="text-[11px] text-zinc-500">Loading segments…</div>
              ) : null}
              <label
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-all ${
                  isSpecific
                    ? 'border-emerald-500/40 bg-emerald-500/5'
                    : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.14]'
                }`}
              >
                <input
                  type="radio"
                  name="segment"
                  value={SPECIFIC_USERS}
                  checked={isSpecific}
                  onChange={() => setSelectedSegment(SPECIFIC_USERS)}
                  className="accent-emerald-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium text-white">Specific users</div>
                  <div className="text-[10.5px] text-zinc-500 truncate">
                    Search and pick one or more people · send now only
                  </div>
                </div>
                <span className="text-[11.5px] font-mono text-emerald-400">
                  {formatIndian(recipients.length)}
                </span>
              </label>
            </div>
            {isSpecific ? <UserPicker selected={recipients} onChange={setRecipients} /> : null}
            {selectedSegment === 'below_version' ? (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[11.5px] text-zinc-400">Send to users below version</span>
                <input
                  value={belowVersion}
                  onChange={e => setBelowVersion(e.target.value.trim())}
                  placeholder="1.1.0"
                  className={`w-24 px-3 py-1.5 rounded-lg bg-white/[0.04] border text-[12.5px] font-mono text-white focus:outline-none ${
                    versionValid ? 'border-white/[0.06] focus:border-emerald-500/30' : 'border-red-500/40'
                  }`}
                />
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-2">When</label>
            <div className="grid grid-cols-2 gap-1.5">
              {(['now', 'schedule'] as const).map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  disabled={isSpecific && m === 'schedule'}
                  title={isSpecific && m === 'schedule' ? 'Only segments can be scheduled' : undefined}
                  className={`rounded-lg border px-3 py-2 text-[12.5px] font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                    effectiveMode === m
                      ? 'border-emerald-500/40 bg-emerald-500/5 text-white'
                      : 'border-white/[0.06] bg-white/[0.02] text-zinc-400 hover:border-white/[0.14]'
                  }`}
                >
                  {m === 'now' ? 'Send now' : 'Schedule'}
                </button>
              ))}
            </div>
            {effectiveMode === 'schedule' ? (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="date"
                  value={sendDate}
                  onChange={e => setSendDate(e.target.value)}
                  className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12.5px] text-white [color-scheme:dark] focus:outline-none focus:border-emerald-500/30"
                />
                <input
                  type="time"
                  value={sendTime}
                  onChange={e => setSendTime(e.target.value)}
                  className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[12.5px] text-white [color-scheme:dark] focus:outline-none focus:border-emerald-500/30"
                />
                <span className="text-[11.5px] text-zinc-500">IST</span>
              </div>
            ) : null}
          </div>

          <button
            onClick={send}
            disabled={sending || !canSend}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-black font-semibold text-sm hover:from-emerald-400 hover:to-emerald-500 disabled:opacity-40 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/10"
          >
            {effectiveMode === 'schedule' ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {sending
              ? 'Saving...'
              : effectiveMode === 'schedule'
                ? sendAtIso
                  ? `Schedule for ${formatSentAt(sendAtIso)}`
                  : 'Pick a date and time'
                : isSpecific && recipients.length > 0
                  ? `Send to ${recipients.length} ${recipients.length === 1 ? 'user' : 'users'}`
                  : 'Send Notification'}
          </button>

          {result && (
            <div className={`px-4 py-2.5 rounded-lg text-[12px] font-medium ${
              result.type === 'success'
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {result.message}
            </div>
          )}
        </div>

        {/* Phone Preview */}
        <div className="flex justify-center">
          <div className="w-[280px]">
            <p className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold mb-3 text-center">Preview</p>
            <div className="bg-zinc-900 rounded-[2rem] p-3 border border-white/[0.06] shadow-2xl">
              {/* Phone notch */}
              <div className="w-20 h-5 bg-black rounded-full mx-auto mb-4" />

              {/* Notification card */}
              <div className="bg-white/[0.06] backdrop-blur rounded-2xl p-4 mx-1 mb-3 border border-white/[0.04]">
                <div className="flex items-start gap-3">
                  <FincurryLogo size="xs" className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider">Fincurry</p>
                      <p className="text-[9px] text-zinc-600">now</p>
                    </div>
                    <p className="text-[13px] font-semibold text-white leading-snug">
                      {title || 'Notification Title'}
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">
                      {body || 'Your notification message will appear here...'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Fake content */}
              <div className="space-y-2 mx-1 opacity-30">
                <div className="h-3 bg-white/5 rounded-full w-3/4" />
                <div className="h-3 bg-white/5 rounded-full w-1/2" />
                <div className="h-20 bg-white/5 rounded-xl mt-3" />
              </div>

              {/* Home indicator */}
              <div className="w-24 h-1 bg-white/20 rounded-full mx-auto mt-6" />
            </div>
          </div>
        </div>
      </div>

      {/* SCHEDULED */}
      <div className="mt-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <div className="mb-4">
          <h2 className="text-[15px] font-semibold text-white">Scheduled</h2>
          <p className="text-[12px] text-zinc-500">
            Sent by the server at the set time · recipients are picked at send time · last 20
          </p>
        </div>
        {scheduled.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-[13px]">Nothing scheduled</div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-white/[0.06]">
            <table className="w-full">
              <thead className="bg-white/[0.03]">
                <tr>
                  {['Title', 'Segment', 'Send at', 'Status', ''].map(h => (
                    <th
                      key={h}
                      className="px-4 py-2 text-left text-[10px] font-semibold text-zinc-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scheduled.map(s => (
                  <tr key={s.id} className="border-t border-white/[0.04]">
                    <td className="max-w-[260px] px-4 py-3">
                      <div className="truncate text-[13px] font-medium text-white">{s.title}</div>
                      <div className="truncate text-[11.5px] text-zinc-500">{s.body}</div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-zinc-400">
                      {s.segment === 'below_version'
                        ? `Below ${s.segmentParams?.belowVersion ?? '?'}`
                        : segments.find(x => x.id === s.segment)?.label ?? s.segment}
                    </td>
                    <td className="px-4 py-3 text-[11.5px] font-mono text-zinc-400">
                      {formatSentAt(s.sendAt)}
                    </td>
                    <td className={`px-4 py-3 text-[12px] font-medium ${STATUS_STYLE[s.status]}`}>
                      {s.status}
                      {s.status === 'sent' && s.recipientCount != null
                        ? ` · ${formatIndian(s.withToken ?? 0)}/${formatIndian(s.recipientCount)} pushed`
                        : ''}
                      {s.status === 'failed' && s.error ? (
                        <div className="text-[11px] text-red-400/70 truncate max-w-[200px]">{s.error}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.status === 'pending' ? (
                        <button
                          onClick={() => cancelScheduled(s.id)}
                          className="text-[11.5px] text-red-400 hover:text-red-300"
                        >
                          Cancel
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* HISTORY */}
      <div className="mt-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h2 className="text-[15px] font-semibold text-white">
              Recent broadcasts
            </h2>
            <p className="text-[12px] text-zinc-500">
              Grouped by title + body · admin_broadcast only · last 20
            </p>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="py-10 text-center text-zinc-500 text-[13px]">
            No broadcasts yet
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-white/[0.06]">
            <table className="w-full">
              <thead className="bg-white/[0.03]">
                <tr>
                  {['Title', 'Body', 'Recipients', 'Sent'].map(h => (
                    <th
                      key={h}
                      className="px-4 py-2 text-left text-[10px] font-semibold text-zinc-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((b, i) => (
                  <tr
                    key={`${b.title}-${b.sentAt}-${i}`}
                    className="border-t border-white/[0.04]"
                  >
                    <td className="max-w-[220px] truncate px-4 py-3 text-[13px] font-medium text-white">
                      {b.title}
                    </td>
                    <td className="max-w-[360px] truncate px-4 py-3 text-[12px] text-zinc-400">
                      {b.body}
                    </td>
                    <td className="px-4 py-3 text-[12.5px] font-mono text-emerald-400">
                      {formatIndian(b.recipientCount)}
                    </td>
                    <td className="px-4 py-3 text-[11.5px] font-mono text-zinc-500">
                      {formatSentAt(b.sentAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
